import React, { useState, useEffect, useRef } from 'react';
import { X, Loader, Plus } from 'lucide-react';
import { projectNamesService } from './projectNamesService';
import { useAuth } from '../../hooks/useAuth';

interface AddProjectNameModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  existingTags?: string[];
}

// Enhanced Tag Chip Input Component (same as Logos module)
interface TagChipInputProps {
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  existingTags: string[];
  placeholder?: string;
}

const TagChipInput: React.FC<TagChipInputProps> = ({ tags, onTagsChange, existingTags, placeholder }) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputValue.trim().length > 0) {
      const filtered = existingTags
        .filter(tag => 
          tag.toLowerCase().includes(inputValue.toLowerCase()) &&
          !tags.includes(tag)
        )
        .slice(0, 8);
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
  }, [inputValue, tags, existingTags]);

  const addTag = (tag: string) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      onTagsChange([...tags, trimmedTag]);
      setInputValue('');
      setShowSuggestions(false);
    }
  };

  const removeTag = (tagToRemove: string) => {
    onTagsChange(tags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (showSuggestions && filteredSuggestions.length > 0) {
        addTag(filteredSuggestions[0]);
      } else if (inputValue.trim()) {
        addTag(inputValue);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    addTag(suggestion);
    inputRef.current?.focus();
  };

  const isExistingTag = (tag: string) => {
    return existingTags.includes(tag);
  };

  return (
    <div>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (inputValue.trim().length > 0) setShowSuggestions(true);
          }}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          className="w-full p-3 border rounded-lg"
          placeholder={placeholder || "Add tags..."}
        />
        
        {/* Add Button */}
        {inputValue.trim() && (
          <button
            type="button"
            onClick={() => addTag(inputValue)}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-primary transition-colors"
          >
            <Plus size={20} />
          </button>
        )}
        
        {/* Suggestions Dropdown */}
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 z-10 max-h-48 overflow-y-auto">
            {filteredSuggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 flex items-center justify-between text-sm"
              >
                <span>{suggestion}</span>
                {index === 0 && (
                  <span className="text-xs text-gray-400">Press Enter</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Tag Chips */}
      {tags.length > 0 && (
        <div className="mt-3">
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                  isExistingTag(tag)
                    ? 'bg-blue-100 text-blue-800 border border-blue-200'
                    : 'bg-green-100 text-green-800 border border-green-200'
                }`}
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="hover:text-red-600 transition-colors"
                >
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              Existing tags
            </span>
            <span className="ml-4 inline-flex items-center gap-1">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              New tags
            </span>
          </p>
        </div>
      )}

      <p className="text-xs text-muted-foreground mt-1">
        Type to see suggestions, press Enter to add new tags, or click suggestions to add existing ones.
      </p>
    </div>
  );
};

export const AddProjectNameModal: React.FC<AddProjectNameModalProps> = ({ isOpen, onClose, onSuccess, existingTags = [] }) => {
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    project: '',
    tags: [] as string[],
    notes: '',
    domainOwned: false
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!formData.name.trim()) {
      setError('Project name is required');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await projectNamesService.addProjectName({
        name: formData.name.trim(),
        project: formData.project.trim() || 'Untitled Project',
        tags: formData.tags,
        notes: formData.notes.trim(),
        domainOwned: formData.domainOwned,
        claimed: false,
        deleted: false,
        createdBy: user.email!
      });

      // Reset form and close modal
      setFormData({ name: '', project: '', tags: [], notes: '', domainOwned: false });
      onSuccess();
      onClose();
    } catch (error: any) {
      setError(error.message || 'Failed to add project name');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setFormData({ name: '', project: '', tags: [], notes: '', domainOwned: false });
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Add Project Name</h2>
            <button
              onClick={handleClose}
              disabled={submitting}
              className="text-muted-foreground hover:text-foreground"
            >
              <X size={24} />
            </button>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Form fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Project Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-3 border rounded-lg"
                  placeholder="Enter project name (e.g., AquaFlow, TechNova)"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Original Project</label>
                <input
                  type="text"
                  value={formData.project}
                  onChange={(e) => setFormData(prev => ({ ...prev, project: e.target.value }))}
                  className="w-full p-3 border rounded-lg"
                  placeholder="Project where this name was created"
                />
              </div>
            </div>

            <div>
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={formData.domainOwned}
                  onChange={(e) => setFormData(prev => ({ ...prev, domainOwned: e.target.checked }))}
                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <div>
                  <span className="text-sm font-medium">We own the domain</span>
                  <p className="text-xs text-muted-foreground">Check if the domain for this project name is owned by the company</p>
                </div>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Tags</label>
              <TagChipInput
                tags={formData.tags}
                onTagsChange={(tags) => setFormData(prev => ({ ...prev, tags }))}
                existingTags={existingTags}
                placeholder="Add tags (e.g., tech, modern, startup)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full p-3 border rounded-lg h-24"
                placeholder="Additional notes or context about this project name"
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-accent disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!formData.name.trim() || submitting}
                className="flex-1 bg-primary text-primary-foreground px-4 py-2 rounded-lg border border-primary flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-primary/90"
              >
                {submitting ? (
                  <>
                    <Loader className="animate-spin" size={16} />
                    Adding...
                  </>
                ) : (
                  'Add Project Name'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};